import React, { useEffect, useMemo, useState } from "react";
import Modal from "../../components/Modal";
import {
  COMPASS_COLLECTIONS,
  findCompassCollection,
  isCollectionAvailable,
  formatPayrollNo,
} from "./compassCollections";
import { parseCompassFilter } from "../../utility/compassFilterParse";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { CONVEX_CONFIGURED } from "../../utility/backendConfig";
import {
  MONGODB_AWSGW_QUERIES,
  MONGODB_AWSGW_APIKEY,
  MONGODB_DATASOURCE_TS,
} from "../../utility/keys";
import CompassMongoBrowser from "./CompassMongoBrowser";
import CompassConvexBrowser from "./CompassConvexBrowser";
import CompassDiffBrowser from "./CompassDiffBrowser";
import CompassDocViewer from "./CompassDocViewer";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Compass (#191) — in-app data browser (the 🧭 header button).
//
// A MongoDB-Compass-style browser for the Convex mirror AND the live Mongo
// collections, scoped to how Leland actually uses Compass: pick a collection,
// pick UAT/PROD, search (quick field, raw filter, or natural language), and
// page through results with controls that don't make you suffer. READ ONLY in
// Phase 1; Phase 2 adds inline edit/delete/import routed through the real
// Mongo mutation + EventLog path so admin edits land in Time Machine.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const SOURCE_TO_DB = { uat: "UAT", prod: "Warehouses" };
const PAGE_SIZES = [50, 100, 250, 500];

function Segmented({ value, options, onChange, disabledValues = [] }) {
  return (
    <div className="inline-flex border-2 border-skin-base rounded overflow-hidden text-xs">
      {options.map((opt) => {
        const disabled = disabledValues.includes(opt.value);
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(opt.value)}
            className="px-3 py-1 disabled:opacity-30"
            style={
              active
                ? { backgroundColor: "rgb(var(--color-button-accent))", color: "#000" }
                : undefined
            }
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function CompassModal({ setOpenCompassModal }) {
  const close = () => setOpenCompassModal(false);

  const [backend, setBackend] = useState("mongo");
  const [source, setSource] = useState("uat");
  const [selectedKey, setSelectedKey] = useState(COMPASS_COLLECTIONS[0].key);
  const [pageSize, setPageSize] = useState(50);

  const [searchFieldIdx, setSearchFieldIdx] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  // The input is bound to `searchValue` (instant); queries use the debounced
  // value so a live prefix scan only fires ~300ms after typing stops.
  const debouncedSearchValue = useDebouncedValue(searchValue, 300);

  const [filterText, setFilterText] = useState("");
  const [appliedFilter, setAppliedFilter] = useState({});
  const [filterError, setFilterError] = useState("");

  const [nlPrompt, setNlPrompt] = useState("");
  const [nlBusy, setNlBusy] = useState(false);
  const [nlNote, setNlNote] = useState("");

  const [selectedDoc, setSelectedDoc] = useState(null);

  const collection = findCompassCollection(selectedKey);
  const convexAvailable =
    CONVEX_CONFIGURED && isCollectionAvailable(collection, "convex");

  // If the chosen collection isn't mirrored in Convex, force the Mongo backend
  // (both "convex" and "both" require the Convex mirror).
  useEffect(() => {
    if ((backend === "convex" || backend === "both") && !convexAvailable) {
      setBackend("mongo");
    }
  }, [backend, convexAvailable]);

  // Switch collection with a FULLY SYNCHRONOUS reset of all search/filter/NL
  // state (batched in one update so the new collection never renders paired
  // with the old filter — the cause of the "0 documents, page 1 of 1" stale-
  // filter bug). The browser is also remounted via `key` below for a pristine
  // pager.
  const selectCollection = (key) => {
    setSelectedKey(key);
    setSearchFieldIdx(0);
    setSearchValue("");
    setFilterText("");
    setAppliedFilter({});
    setFilterError("");
    setNlPrompt("");
    setNlNote("");
    setSelectedDoc(null);
  };

  const qs = collection?.quickSearch?.[searchFieldIdx] || null;

  const runFilter = () => {
    const res = parseCompassFilter(filterText);
    if (!res.ok) {
      setFilterError(res.error);
      return;
    }
    setFilterError("");
    setAppliedFilter(res.filter);
    setNlNote(res.relaxed ? "Filter interpreted (relaxed) and applied." : "");
  };

  const clearAll = () => {
    setSearchValue("");
    setFilterText("");
    setAppliedFilter({});
    setFilterError("");
    setNlPrompt("");
    setNlNote("");
  };

  // Clear just the raw-filter box (and the applied filter it produced).
  const clearRawFilter = () => {
    setFilterText("");
    setAppliedFilter({});
    setFilterError("");
    setNlNote("");
  };

  // Clear just the plain-English prompt + its status note.
  const clearNl = () => {
    setNlPrompt("");
    setNlNote("");
  };

  const generateNl = async () => {
    if (!nlPrompt.trim() || !collection) return;
    setNlBusy(true);
    setNlNote("");
    try {
      const body = {
        method: "nlToFilter",
        dataSource: MONGODB_DATASOURCE_TS,
        database: SOURCE_TO_DB[source],
        collection: collection.mongoCollection,
        collectionName: collection.label,
        prompt: nlPrompt,
        fieldHints: collection.quickSearch.map((q) => q.mongoField),
      };
      const resp = await fetch(MONGODB_AWSGW_QUERIES, {
        method: "POST",
        headers: {
          "x-api-key": MONGODB_AWSGW_APIKEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const json = await resp.json();
      const out = json?.message?.result;
      if (out?.error) {
        setNlNote(`⚠️ ${out.error}`);
      } else if (out?.filter) {
        const text = JSON.stringify(out.filter);
        setFilterText(text);
        setFilterError("");
        setNlNote("Generated — review the filter, then click Run.");
      } else {
        setNlNote("⚠️ No filter returned.");
      }
    } catch (e) {
      setNlNote(`⚠️ ${e.message}`);
    } finally {
      setNlBusy(false);
    }
  };

  // Per-backend search wiring.
  const convexSearch = useMemo(() => {
    // server-side indexed pin if possible; otherwise fold into the client filter
    if (qs && qs.convexField && debouncedSearchValue) {
      const value = qs.format === "payrollNo" ? formatPayrollNo(debouncedSearchValue) : debouncedSearchValue;
      return { convexSearchField: qs.convexField, convexSearchValue: value, clientFilter: appliedFilter };
    }
    if (qs && !qs.convexField && debouncedSearchValue) {
      return {
        convexSearchField: null,
        convexSearchValue: null,
        clientFilter: { ...appliedFilter, [qs.mongoField]: debouncedSearchValue },
      };
    }
    return { convexSearchField: null, convexSearchValue: null, clientFilter: appliedFilter };
  }, [qs, debouncedSearchValue, appliedFilter]);

  // Grid columns: use the catalog's explicit `columns` when defined (most
  // collections — _id intentionally dropped); otherwise fall back to
  // _id + the quick-search fields (collections without a curated column list).
  const previewFields = useMemo(() => {
    if (collection?.columns) return collection.columns;
    const fromSearch = (collection?.quickSearch || [])
      .filter((q) => q.mongoField !== "_id")
      .map((q) => ({ label: q.label, path: q.mongoField }));
    return [{ label: "_id", path: "_id" }, ...fromSearch];
  }, [collection]);

  const selectedId = selectedDoc?._id ?? null;

  return (
    <Modal onClose={close} disableCloseOnClickOutSide>
      <Modal.Header>
        <span className="flex items-center gap-2">
          <span aria-hidden="true">🧭</span> Compass — Data Browser
          <span className="text-xs font-normal text-skin-link">
            read-only ·{" "}
            {backend === "both"
              ? "Both (Mongo ⟷ Convex diff)"
              : backend === "convex"
              ? "Convex (live)"
              : "MongoDB"}{" "}
            · {source === "prod" ? "PRODUCTION" : "UAT"}
          </span>
        </span>
      </Modal.Header>

      <Modal.Content>
        <div className="flex gap-3" style={{ width: "92vw", height: "78vh" }}>
          {/* Left: collection list */}
          <div className="w-52 shrink-0 overflow-auto border-r-2 border-skin-base pr-2">
            <div className="text-xs uppercase text-skin-link mb-1">Collections</div>
            {COMPASS_COLLECTIONS.map((c) => {
              const active = c.key === selectedKey;
              const mirrored = Boolean(c.convexTable);
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => selectCollection(c.key)}
                  className="block w-full text-left px-2 py-1 rounded text-sm hover:bg-skin-base-hover"
                  style={
                    active
                      ? { backgroundColor: "rgb(var(--color-button-accent))", color: "#000" }
                      : undefined
                  }
                >
                  {c.label}
                  {!mirrored && (
                    <span className="block text-[10px] opacity-70">Mongo-only</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Right: toolbar + search + results + viewer */}
          <div className="flex-1 min-w-0 flex flex-col gap-2">
            {/* Toolbar */}
            <div className="flex items-center gap-4 flex-wrap">
              <label className="flex items-center gap-2 text-xs">
                Backend
                <Segmented
                  value={backend}
                  onChange={setBackend}
                  options={[
                    { value: "mongo", label: "MongoDB" },
                    { value: "convex", label: "Convex (live)" },
                    { value: "both", label: "Both (diff)" },
                  ]}
                  disabledValues={convexAvailable ? [] : ["convex", "both"]}
                />
              </label>
              <label className="flex items-center gap-2 text-xs">
                Source
                <Segmented
                  value={source}
                  onChange={setSource}
                  options={[
                    { value: "uat", label: "UAT" },
                    { value: "prod", label: "PROD" },
                  ]}
                />
              </label>
              <label className="flex items-center gap-2 text-xs">
                Page size
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="text-black rounded pl-2 pr-7 py-0.5 w-20"
                >
                  {PAGE_SIZES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              {source === "prod" && (
                <span className="text-xs text-red font-medium">⚠ PRODUCTION data</span>
              )}
            </div>

            {/* Search row */}
            <div className="flex items-center gap-2 flex-wrap text-xs">
              <span className="text-skin-link">Quick search</span>
              <select
                value={searchFieldIdx}
                onChange={(e) => setSearchFieldIdx(Number(e.target.value))}
                className="text-black rounded px-1 py-0.5"
              >
                {(collection?.quickSearch || []).map((q, i) => (
                  <option key={q.mongoField} value={i}>
                    {q.label}
                  </option>
                ))}
              </select>
              <input
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={qs ? `value for ${qs.label}…` : "value…"}
                className="text-black rounded px-2 py-0.5 w-56"
              />
              <button
                type="button"
                onClick={clearAll}
                className="px-2 py-1 border-2 border-skin-base rounded"
              >
                Clear
              </button>
            </div>

            {/* Raw filter + NL */}
            <div className="flex gap-2 flex-wrap items-start text-xs">
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-skin-link">Raw filter (JSON)</span>
                  <button
                    type="button"
                    onClick={runFilter}
                    className="px-3 py-1 border-2 border-skin-link bg-skin-base rounded font-medium shadow-sm hover:bg-skin-base-hover"
                  >
                    Run
                  </button>
                  <button
                    type="button"
                    onClick={clearRawFilter}
                    className="px-3 py-1 border-2 border-skin-base rounded hover:bg-skin-base-hover"
                  >
                    Clear
                  </button>
                  {backend === "convex" && (
                    <span className="opacity-70">applied to page (client-side)</span>
                  )}
                </div>
                <textarea
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  placeholder='e.g. { "statusCode": "4" }'
                  rows={2}
                  className="w-full text-black rounded px-2 py-1 font-mono"
                />
                {filterError && <div className="text-red mt-1">{filterError}</div>}
              </div>
              <div className="flex-1 min-w-[260px]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-skin-link">Ask in plain English</span>
                  <button
                    type="button"
                    onClick={generateNl}
                    disabled={nlBusy || !nlPrompt.trim()}
                    className="px-3 py-1 border-2 border-skin-link bg-skin-base rounded font-medium shadow-sm hover:bg-skin-base-hover disabled:opacity-40"
                  >
                    {nlBusy ? "Generating…" : "Generate filter"}
                  </button>
                  <button
                    type="button"
                    onClick={clearNl}
                    className="px-3 py-1 border-2 border-skin-base rounded hover:bg-skin-base-hover"
                  >
                    Clear
                  </button>
                </div>
                <textarea
                  value={nlPrompt}
                  onChange={(e) => setNlPrompt(e.target.value)}
                  placeholder="e.g. candidates in BHM on scheduling hold"
                  rows={2}
                  className="w-full text-black rounded px-2 py-1"
                />
                {nlNote && <div className="mt-1 opacity-80">{nlNote}</div>}
              </div>
            </div>

            {/* Results + viewer — OR the full-width Both diff (no doc list). */}
            {backend === "both" ? (
              <div className="flex-1 min-h-0">
                {!collection ? (
                  <div className="text-skin-link italic p-4">No collection selected.</div>
                ) : !collection.convexTable ? (
                  <div className="text-skin-link italic p-4">
                    This collection isn’t mirrored in Convex — switch off “Both”.
                  </div>
                ) : (
                  <CompassDiffBrowser
                    key={`both|${source}|${selectedKey}`}
                    source={source}
                    mongoCollection={collection.mongoCollection}
                    convexTable={collection.convexTable}
                    rawFilter={appliedFilter}
                    searchField={qs ? qs.mongoField : null}
                    searchValue={debouncedSearchValue}
                    pageSize={pageSize}
                  />
                )}
              </div>
            ) : (
              <div className="flex gap-3 flex-1 min-h-0">
                <div className="flex-1 min-w-0 border-2 border-skin-base rounded p-2 flex flex-col min-h-0">
                  {!collection ? (
                    <div className="text-skin-link italic p-4">No collection selected.</div>
                  ) : backend === "convex" ? (
                    <CompassConvexBrowser
                      key={`convex|${source}|${selectedKey}`}
                      source={source}
                      table={collection.convexTable}
                      convexSearchField={convexSearch.convexSearchField}
                      convexSearchValue={convexSearch.convexSearchValue}
                      clientFilter={convexSearch.clientFilter}
                      pageSize={pageSize}
                      previewFields={previewFields}
                      selectedId={selectedId}
                      onSelect={setSelectedDoc}
                    />
                  ) : (
                    <CompassMongoBrowser
                      key={`mongo|${source}|${selectedKey}`}
                      source={source}
                      mongoCollection={collection.mongoCollection}
                      rawFilter={appliedFilter}
                      searchField={qs ? qs.mongoField : null}
                      searchValue={debouncedSearchValue}
                      pageSize={pageSize}
                      previewFields={previewFields}
                      selectedId={selectedId}
                      onSelect={setSelectedDoc}
                    />
                  )}
                </div>
                <div className="w-[34%] shrink-0 border-2 border-skin-base rounded p-2 min-h-0">
                  <CompassDocViewer doc={selectedDoc} />
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal.Content>
    </Modal>
  );
}

export default CompassModal;
